import { NavLink, useNavigate } from "react-router-dom";
import {
  GridFour,
  Calculator,
  Database,
  Calendar,
  CurrencyDollar,
  TrendUp,
  ArrowsLeftRight,
  Tree,
  ShieldCheck,
  ChatCircleDots,
  Folder,
  SignOut,
  Clock,
  CreditCard,
  UsersThree,
  Moon,
  Sun,
  Globe,
  Wind,
  Warning,
  Question,
  Lock,
  Timer,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import { useTheme } from "@/context/ThemeContext";

const NAV = [
  { to: "/dashboard", label: "Executive Dashboard", icon: GridFour, id: "dashboard" },
  { to: "/projects", label: "Projects", icon: Folder, id: "projects" },
  { to: "/calculator", label: "Embodied Calculator", icon: Calculator, id: "calculator" },
  { to: "/epds", label: "EPD Database", icon: Database, id: "epds" },
  { to: "/monthly", label: "Monthly Tracking", icon: Calendar, id: "monthly" },
  { to: "/capture", label: "Carbon Capture", icon: Wind, id: "capture" },
  { to: "/cost", label: "Carbon Cost", icon: CurrencyDollar, id: "cost" },
  { to: "/forecast", label: "Forecast", icon: TrendUp, id: "forecast" },
  { to: "/optimizer", label: "Optimizer", icon: ArrowsLeftRight, id: "optimizer" },
  { to: "/procurement", label: "Procurement Lock-in", icon: Clock, id: "procurement" },
  { to: "/schedule", label: "Programme Twin · 5D", icon: Timer, id: "schedule" },
  { to: "/risk", label: "Climate Risk", icon: Warning, id: "risk" },
  { to: "/geographic", label: "Geographic Intelligence", icon: Globe, id: "geographic" },
  { to: "/impact", label: "Impact", icon: Tree, id: "impact" },
  { to: "/compliance", label: "Compliance", icon: ShieldCheck, id: "compliance" },
];

const ACCOUNT_NAV = [
  { to: "/organization", label: "Organisation", icon: UsersThree, id: "organization" },
  { to: "/billing", label: "Billing", icon: CreditCard, id: "billing" },
  { to: "/security", label: "Security · 2FA", icon: Lock, id: "security" },
  { to: "/help", label: "Help Centre", icon: Question, id: "help" },
];

export default function Sidebar({ onOpenCopilot }) {
  const { user, logout } = useAuth();
  const { projects, active, selectProject } = useProjects();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <aside
      data-testid="sidebar"
      className="w-[260px] shrink-0 border-r theme-border theme-bg flex flex-col h-screen sticky top-0"
    >
      <div className="px-6 pt-6 pb-5 border-b theme-border">
        <div className="overline">GPI</div>
        <div className="font-serif-display text-2xl theme-ink mt-1 leading-none">CarbonIQ</div>
        <div className="text-[11px] theme-ink-dim mt-2 tracking-wider">5D · CARBON × COST × TIME</div>
      </div>

      <div className="px-4 py-4 border-b theme-border">
        <div className="overline mb-2">Active Project</div>
        <select
          data-testid="active-project-select"
          value={active?.id || ""}
          onChange={(e) => selectProject(e.target.value)}
          className="w-full theme-input border text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-gold transition-colors"
        >
          {projects.length === 0 && <option value="">No projects yet</option>}
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      <nav className="flex-1 overflow-y-auto py-2">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.id}`}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-6 py-2.5 text-sm transition-colors duration-150 ${
                  isActive ? "theme-ink bg-gold/[0.04]" : "theme-ink-muted hover:theme-ink hover:bg-black/[0.02] dark:hover:bg-white/[0.02]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-gold" />}
                  <Icon size={16} weight={isActive ? "fill" : "regular"} />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
        <div className="px-6 pt-4 pb-1 overline">Account</div>
        {ACCOUNT_NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.id}`}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-6 py-2.5 text-sm transition-colors duration-150 ${
                  isActive ? "theme-ink bg-gold/[0.04]" : "theme-ink-muted hover:theme-ink"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-gold" />}
                  <Icon size={16} weight={isActive ? "fill" : "regular"} />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-4 py-3 border-t theme-border space-y-2">
        <button
          data-testid="open-copilot-btn"
          onClick={onOpenCopilot}
          className="w-full flex items-center justify-center gap-2 bg-gold text-black text-sm font-medium py-2.5 rounded-sm hover:bg-[#e0bc40] transition-colors"
        >
          <ChatCircleDots size={16} weight="bold" />
          Ask Carbon Copilot
        </button>
        <button
          data-testid="theme-toggle-btn"
          onClick={toggle}
          className="w-full flex items-center justify-center gap-2 border theme-border text-xs theme-ink-muted py-2 rounded-sm hover:theme-ink transition-colors"
        >
          {theme === "dark" ? <Sun size={14} weight="fill" /> : <Moon size={14} weight="fill" />}
          {theme === "dark" ? "Light mode" : "Dark mode"}
        </button>
      </div>

      <div className="px-6 py-4 border-t theme-border flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-xs theme-ink-muted truncate">{user?.email}</div>
          <div className="text-[11px] theme-ink-dim">{user?.role}</div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="theme-ink-muted hover:theme-ink transition-colors"
          aria-label="Sign out"
        >
          <SignOut size={16} />
        </button>
      </div>
    </aside>
  );
}
